import { io, Socket } from 'socket.io-client';
import { IMessagingProvider, IMessage } from '../IMessagingProvider';
import axios from 'axios';
import { NotificationService } from '../../../utils/notifications';
import { encryptMessage, decryptMessage, getKeyPair, encryptMessageForDevices } from '../../encryption';
import { getDeviceInfo } from '../../deviceFingerprint';
import { getUserDevices, registerDevice } from '../../../api/deviceApi';
import { API_BASE_URL, getWebSocketURL } from '../../../config/api.config';

// Server-side messaging provider - messages stored in MySQL database
export class ServerMessagingProvider implements IMessagingProvider {
  private socket: Socket | null = null;
  private currentUserId: string;
  private senderName: string = '';
  private currentDeviceId: string = '';
  private messageCallbacks: Array<(msg: IMessage) => void> = [];
  private onlineStatusCallbacks: Array<(userId: string, isOnline: boolean) => void> = [];
  private onlineUsers: Set<string> = new Set();

  constructor(userId: string, senderName?: string) {
    this.currentUserId = userId;
    this.senderName = senderName || '';

    console.log(`📱 [ServerMessagingProvider] Constructor called for user: ${userId}`);

    // Get device info and register device (pass userId for per-user device ID)
    const deviceInfo = getDeviceInfo(userId);
    this.currentDeviceId = deviceInfo.deviceId;

    console.log(`📱 [ServerMessagingProvider] Current device ID: ${this.currentDeviceId}`);
    console.log(`📱 [ServerMessagingProvider] Device info:`, deviceInfo);

    // Register device asynchronously - DO NOT BLOCK message loading
    this.registerDeviceIfNeeded(deviceInfo);
  }

  // Helper to get axios config with device ID header
  private getAxiosConfig(additionalConfig: any = {}): any {
    return {
      ...additionalConfig,
      withCredentials: true,
      headers: {
        ...additionalConfig.headers,
        'x-device-id': this.currentDeviceId
      }
    };
  }

  private async registerDeviceIfNeeded(deviceInfo: ReturnType<typeof getDeviceInfo>): Promise<void> {
    console.log(`📱 [ServerMessagingProvider] registerDeviceIfNeeded called for device: ${deviceInfo.deviceId}`);

    try {
      const keyPair = getKeyPair(this.currentUserId);
      if (!keyPair) {
        console.error(`❌ [ServerMessagingProvider] No encryption keys found for user ${this.currentUserId} - device registration skipped`);
        console.error(`❌ [ServerMessagingProvider] This means messages cannot be encrypted/decrypted!`);
        return;
      }

      console.log(`🔐 [ServerMessagingProvider] Found encryption keys, registering device...`);

      const response = await registerDevice({
        deviceId: deviceInfo.deviceId,
        deviceName: deviceInfo.deviceName,
        deviceType: deviceInfo.deviceType,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        publicKey: keyPair.publicKey
      });

      console.log(`✅ [ServerMessagingProvider] Device registered successfully:`, deviceInfo.deviceName);
      console.log(`✅ [ServerMessagingProvider] Registration response:`, response);
    } catch (error) {
      console.error(`❌ [ServerMessagingProvider] Failed to register device:`, error);
      console.error(`❌ [ServerMessagingProvider] Messages may not decrypt correctly!`);
    }
  }

  onUserOnlineStatusChanged(callback: (userId: string, isOnline: boolean) => void): void {
    this.onlineStatusCallbacks.push(callback);
  }

  async connect(): Promise<void> {
    // Connect to signaling server - use special WebSocket URL for dev mode
    const socketURL = getWebSocketURL();
    console.log('🔌 Connecting to WebSocket:', socketURL || '(same origin)');

    this.socket = io(socketURL, {
      withCredentials: true,
      transports: ['websocket', 'polling']
    });

    return new Promise((resolve, reject) => {
      if (!this.socket) return reject('Socket not initialized');

      this.socket.on('connect', () => {
        this.socket!.emit('join', this.currentUserId);
        this.setupSocketListeners();
        resolve();
      });

      this.socket.on('connect_error', (error) => {
        console.error('Connection error:', error);
        reject(error);
      });
    });
  }

  private setupSocketListeners(): void {
    if (!this.socket) return;

    // Listen for real-time messages
    this.socket.on('relayed-message', async ({ fromUserId, encryptedMessage, senderName, senderPublicKey }) => {
      try {
        console.log('📨 Received real-time message from:', fromUserId, 'encrypted:', encryptedMessage.encrypted);
        // Decrypt the message if it's encrypted
        let message: IMessage = { ...encryptedMessage };

        if (encryptedMessage.encrypted && encryptedMessage.content) {
          try {
            const keyPair = getKeyPair(this.currentUserId);
            if (keyPair && senderPublicKey) {
              const decryptedContent = decryptMessage(
                encryptedMessage.content,
                senderPublicKey,
                keyPair.secretKey
              );
              message = {
                ...encryptedMessage,
                content: decryptedContent,
                encrypted: false
              };
            }
          } catch (error) {
            console.error('Failed to decrypt message:', error);
            message.content = '[Encrypted message - decryption failed]';
          }
        }

        // Show browser notification if page not focused
        if (senderName) {
          try {
            await NotificationService.showMessageNotification(
              senderName,
              message.content,
              fromUserId
            );
          } catch (error) {
            console.warn('Failed to show notification:', error);
          }
        }

        console.log('🔔 Triggering', this.messageCallbacks.length, 'message callbacks');
        this.messageCallbacks.forEach(callback => callback(message));
      } catch (error) {
        console.error('Error handling relayed message:', error);
      }
    });

    // Online status
    this.socket.on('user-online', (userId: string) => {
      this.onlineUsers.add(userId);
      this.onlineStatusCallbacks.forEach(callback => callback(userId, true));
    });

    this.socket.on('user-offline', (userId: string) => {
      this.onlineUsers.delete(userId);
      this.onlineStatusCallbacks.forEach(callback => callback(userId, false));
    });
  }

  async sendMessage(recipientId: string, content: string, mediaData?: { mediaType: string; mediaUrl: string; thumbnailUrl?: string; linkPreview?: any }): Promise<IMessage> {
    // Keep original plaintext for local display
    const plaintextContent = content;

    // Encrypt message before sending (multi-device)
    let isEncrypted = false;
    let deviceCopies: Array<{ deviceId: string; encryptedContent: string }> = [];
    let encryptedContent = content; // Will hold encrypted version for storage

    try {
      // Initialize encryption keys if not already set up
      let keyPair = getKeyPair(this.currentUserId);
      if (!keyPair) {
        console.log('No encryption keys found, initializing...');
        const { initializeEncryption } = await import('../../encryption');
        const publicKey = initializeEncryption(this.currentUserId);

        // Upload public key to server
        try {
          await axios.put(`${API_BASE_URL}/api/auth/user/public-key`, { publicKey }, { withCredentials: true });
          console.log('✅ Encryption keys initialized and uploaded');
        } catch (err) {
          console.warn('Failed to upload public key:', err);
        }

        keyPair = getKeyPair(this.currentUserId);
      }

      if (keyPair) {
        // Get recipient's devices AND sender's own devices (for multi-device sync)
        try {
          console.log(`📡 Fetching devices for recipient ${recipientId} and sender ${this.currentUserId}`);
          const [recipientDevicesResponse, senderDevicesResponse] = await Promise.all([
            getUserDevices(recipientId),
            getUserDevices(this.currentUserId)
          ]);

          console.log(`📱 Recipient devices response:`, recipientDevicesResponse);
          console.log(`📱 Sender devices response:`, senderDevicesResponse);

          const allDevices: Array<{ deviceId: string; publicKey: string }> = [];
          const recipientHasDevices = recipientDevicesResponse.success && recipientDevicesResponse.data.devices.length > 0;

          // Add recipient's devices
          if (recipientHasDevices) {
            const recipientDevices = recipientDevicesResponse.data.devices.map((d: any) => ({
              deviceId: d.device_id,
              publicKey: d.public_key
            }));
            console.log(`✅ Adding ${recipientDevices.length} recipient devices:`, recipientDevices.map((d: any) => d.deviceId));
            allDevices.push(...recipientDevices);
          }

          // Add sender's own devices (so they can read on all their devices)
          if (senderDevicesResponse.success && senderDevicesResponse.data.devices.length > 0) {
            const senderDevices = senderDevicesResponse.data.devices.map((d: any) => ({
              deviceId: d.device_id,
              publicKey: d.public_key
            }));
            console.log(`✅ Adding ${senderDevices.length} sender devices:`, senderDevices.map((d: any) => d.deviceId));
            allDevices.push(...senderDevices);
          }

          console.log(`🔐 Total devices to encrypt for: ${allDevices.length}`);

          if (allDevices.length > 0) {
            // Encrypt message for ALL devices (recipient's + sender's)
            deviceCopies = encryptMessageForDevices(plaintextContent, allDevices, keyPair.secretKey);
            isEncrypted = true;
            console.log(`🔒 Message encrypted for ${deviceCopies.length} devices (${recipientDevicesResponse.data?.devices?.length || 0} recipient + ${senderDevicesResponse.data?.devices?.length || 0} sender)`);
          } else if (senderDevicesResponse.success && senderDevicesResponse.data.devices.length > 0) {
            // Recipient has no devices yet, but encrypt for sender's devices so they can read their own sent messages
            const senderDevices = senderDevicesResponse.data.devices.map((d: any) => ({
              deviceId: d.device_id,
              publicKey: d.public_key
            }));
            deviceCopies = encryptMessageForDevices(plaintextContent, senderDevices, keyPair.secretKey);
            isEncrypted = true;
            console.warn(`⚠️ Recipient has no devices - encrypted for sender only (${deviceCopies.length} devices). Recipient must register a device to read messages.`);
          } else {
            // No devices at all - shouldn't happen
            console.error('❌ Cannot encrypt: No devices registered for sender or recipient.');
            throw new Error('No registered devices found. Please refresh and try again.');
          }
        } catch (err) {
          console.error('❌ Failed to encrypt message:', err);
        }
      }
    } catch (error) {
      console.warn('Failed to encrypt message, sending as plain text:', error);
    }

    const message: IMessage = {
      id: this.generateMessageId(),
      senderId: this.currentUserId,
      recipientId,
      content: plaintextContent, // Always use PLAINTEXT for local display
      timestamp: Date.now(),
      status: 'sending',
      encrypted: false, // Message is already decrypted for local display
      ...(mediaData && {
        mediaType: mediaData.mediaType as 'image' | 'video' | 'link',
        mediaUrl: mediaData.mediaUrl,
        thumbnailUrl: mediaData.thumbnailUrl,
        linkPreview: mediaData.linkPreview
      })
    };

    try {
      // Store on server with device-specific encrypted copies
      // If recipient has no devices, message is encrypted ONLY for sender's devices
      // Recipient won't be able to read messages sent before they registered (E2E security trade-off)
      const response = await axios.post(
        `${API_BASE_URL}/api/messages`,
        {
          id: message.id,
          recipientId,
          content: deviceCopies.length > 0 ? deviceCopies[0].encryptedContent : encryptedContent,
          isEncrypted,
          senderDeviceId: this.currentDeviceId,
          encryptionVersion: deviceCopies.length > 0 ? 2 : 1,
          deviceCopies: deviceCopies.length > 0 ? deviceCopies : undefined,
          ...(mediaData && {
            mediaType: mediaData.mediaType,
            mediaUrl: mediaData.mediaUrl,
            thumbnailUrl: mediaData.thumbnailUrl,
            linkPreview: mediaData.linkPreview
          })
        },
        {
          withCredentials: true,
          headers: { 'x-device-id': this.currentDeviceId }
        }
      );

      if (response.data.success) {
        message.status = 'sent';

        // Relay via WebSocket for real-time delivery
        if (this.socket && this.socket.connected) {
          this.socket.emit('relay-message', {
            recipientId,
            encryptedMessage: message,
            senderName: this.senderName
          });
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    }

    return message;
  }

  async getMessages(recipientId: string): Promise<IMessage[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/messages/conversation/${recipientId}?deviceId=${this.currentDeviceId}`,
        this.getAxiosConfig()
      );

      if (response.data.success) {
        const keyPair = getKeyPair(this.currentUserId);

        // Get BOTH users' devices to decrypt messages properly
        // - Need OTHER user's devices to decrypt messages THEY sent
        // - Need OWN devices to decrypt messages I sent (for multi-device sync)
        let otherUserDevicesMap: Map<string, string> = new Map();
        let myDevicesMap: Map<string, string> = new Map();

        try {
          const [otherUserDevicesResponse, myDevicesResponse] = await Promise.all([
            getUserDevices(recipientId),
            getUserDevices(this.currentUserId)
          ]);

          if (otherUserDevicesResponse.success) {
            otherUserDevicesResponse.data.devices.forEach((d: any) => {
              otherUserDevicesMap.set(d.device_id, d.public_key);
            });
          }

          if (myDevicesResponse.success) {
            myDevicesResponse.data.devices.forEach((d: any) => {
              myDevicesMap.set(d.device_id, d.public_key);
            });
          }
        } catch (error) {
          // Silent fail
        }

        // Fallback: try to get legacy single public key for OTHER user
        let legacyOtherUserPublicKey: string | null = null;
        if (otherUserDevicesMap.size === 0) {
          try {
            const keyResponse = await axios.get(
              `${API_BASE_URL}/api/auth/user/${recipientId}/public-key`,
              { withCredentials: true }
            );
            if (keyResponse.data.success) {
              legacyOtherUserPublicKey = keyResponse.data.publicKey;
            }
          } catch (error) {
            // Silent fail
          }
        }

        return response.data.data.messages.map((msg: any) => {
          let content = msg.content;
          const isSentByMe = String(msg.sender_id) === String(this.currentUserId);

          if (msg.is_encrypted && keyPair) {
            try {
              // Multi-device encryption (version 2)
              if (msg.encryption_version === 2) {
                if (msg.device_encrypted_content) {
                  // We have device-specific encrypted content for THIS device
                  // Need to determine which public key to use:
                  // - If I sent it: use MY sender device's public key
                  // - If they sent it: use THEIR sender device's public key
                  const senderPublicKey = isSentByMe
                    ? myDevicesMap.get(msg.sender_device_id)
                    : otherUserDevicesMap.get(msg.sender_device_id);

                  if (senderPublicKey) {
                    content = decryptMessage(msg.device_encrypted_content, senderPublicKey, keyPair.secretKey);
                  } else {
                    content = '[🔒 Encrypted - Could not find sender device key]';
                  }
                } else {
                  content = '[🔒 Encrypted for a different device]';
                }
              }
              // Legacy single-device encryption (version 1)
              else if (legacyOtherUserPublicKey) {
                content = decryptMessage(msg.content, legacyOtherUserPublicKey, keyPair.secretKey);
              } else {
                content = '[🔒 Encrypted - No decryption key]';
              }
            } catch (error) {
              content = '[🔒 Encrypted - Could not decrypt]';
            }
          }

          return {
            id: msg.id,
            senderId: String(msg.sender_id),
            recipientId: String(msg.recipient_id),
            content,
            timestamp: new Date(msg.created_at).getTime(),
            status: msg.status,
            encrypted: false, // Already decrypted
            ...(msg.media_type && {
              mediaType: msg.media_type,
              mediaUrl: msg.media_url,
              thumbnailUrl: msg.thumbnail_url,
              linkPreview: msg.link_preview ? (typeof msg.link_preview === 'string' ? JSON.parse(msg.link_preview) : msg.link_preview) : undefined
            })
          };
        });
      }

      return [];
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      return [];
    }
  }

  async getAllConversations(): Promise<{ userId: string; lastMessage: IMessage | null; unreadCount: number }[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/messages/conversations`,
        {
          withCredentials: true,
          headers: {
            'x-device-id': this.currentDeviceId
          }
        }
      );

      if (response.data.success) {
        return response.data.data.conversations.map((conv: any) => ({
          userId: String(conv.user_id),
          lastMessage: null,
          unreadCount: conv.unread_count || 0
        }));
      }

      return [];
    } catch (error) {
      return [];
    }
  }

  async markAsRead(messageId: string): Promise<void> {
    try {
      await axios.put(
        `${API_BASE_URL}/api/messages/read`,
        { messageIds: [messageId] },
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Failed to mark message as read:', error);
    }
  }

  async deleteMessage(messageId: string): Promise<void> {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/messages/${messageId}`,
        { withCredentials: true }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete message');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete message';
      throw new Error(errorMessage);
    }
  }

  async isOnline(userId: string): Promise<boolean> {
    return this.onlineUsers.has(userId);
  }

  async deleteConversation(userId: string): Promise<void> {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/messages/conversation/${userId}`,
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      throw error;
    }
  }

  onMessageReceived(callback: (message: IMessage) => void): void {
    this.messageCallbacks.push(callback);
  }

  async disconnect(): Promise<void> {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  private generateMessageId(): string {
    return `${this.currentUserId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Required by interface but not used with server storage
  async getUserPublicKey(userId: string): Promise<string | null> {
    return null;
  }

  getPublicKey(): string {
    return '';
  }
}
